import { Component, Input, forwardRef, HostListener, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UserSessionService } from '../../services/user-session.service';

@Component({
	selector: 'app-multi-select-dropdown',
	templateUrl: './multi-select-dropdown.component.html',
	styleUrls: ['./multi-select-dropdown.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MultiSelectDropdownComponent),
			multi: true
		}
	]
})
export class MultiSelectDropdownComponent implements ControlValueAccessor, OnInit {
	// Callers frequently bind a list that is still undefined while its HTTP request is in flight, so the
	// input is normalised here - a null list must never reach writeValue(), which runs before
	// registerOnChange() and would leave the control unable to report selections back to the form.
	@Input()
	set options(value: any[]) {
		this.optionList = value ? value : [];
		this.applyRawValue();
		if (!this.selectAllChecked) {
			this.tickedToAll = false;
		}
	}

	get options(): any[] {
		return this.optionList;
	}

	@Input() idField: string = 'Id';
	@Input() labelField: string = 'Name';
	@Input() placeholder: string = 'Select...';
	@Input() allSelectedLabel: string;
	@Input() selectAllLabel: string = 'Select all';
	@Input() selectedCountLabel: string = 'selected';
	@Input() emptyLabel: string = 'No options available';
	// Shown in the panel while an emptyMeansAll list has no ticks, to say what closing it will do.
	@Input() emptySelectionHint: string = 'Nothing selected - everything will be used when you close';
	// When true, an empty array is treated as "everything selected" on both read and write - matches backends where an empty list means unrestricted access.
	@Input() emptyMeansAll: boolean = false;
	// Names the slot that remembers the ticks a user had before "Select all" took over, so they
	// survive a reload. Left unset nothing is persisted, which is what the form-bound usages want:
	// a remembered list that disagrees with freshly loaded server state confuses more than it helps.
	// Must be unique per *rendered* dropdown - instances repeated per row need the row id in the key,
	// or the rows hand each other's remembered selections around.
	@Input() persistKey: string;

	// When true, ticked options are listed first in the order they were ticked, and that order is what the
	// bound value carries. Off by default so existing dropdowns keep listing and emitting in option order.
	@Input() sortable: boolean = false;

	isOpen = false;
	selectedIds: string[] = [];

	private optionList: any[] = [];
	private rawValue: string[] = [];
	// True while "everything selected" was reached by ticking options one at a time.
	private tickedToAll = false;
	// True while an emptyMeansAll list has had its last tick removed and the panel is still open. The
	// caller keeps the previous value until the panel closes, then "everything" takes over.
	private holdingEmpty = false;
	// Ticks the user had before "Select all" took over, handed back when they uncheck it.
	private rememberedIds: string[] = [];
	private onChange: (value: string[]) => void = () => { };
	private onTouched: () => void = () => { };

	constructor(private userSessionService: UserSessionService) { }

	ngOnInit(): void {
		this.rememberedIds = this.readRememberedIds();
	}

	writeValue(value: string[]): void {
		const incoming = value ? [...value] : [];
		// A parent echoing back what we just emitted is not a new value, so it must not take the
		// panel away from a user who is mid-selection.
		if (!this.sameIds(incoming, this.rawValue)) {
			this.tickedToAll = false;
			this.holdingEmpty = false;
		}

		this.rawValue = incoming;
		this.applyRawValue();
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	getId(option: any): string {
		return option[this.idField];
	}

	getLabel(option: any): string {
		return option[this.labelField];
	}

	isSelected(option: any): boolean {
		return this.selectedIds.includes(this.getId(option));
	}

	toggle(): void {
		if (this.locked) {
			return;
		}

		if (this.isOpen) {
			this.close();
			return;
		}

		this.isOpen = true;
		this.onTouched();
	}

	close(): void {
		this.isOpen = false;
		if (this.holdingEmpty) {
			this.applyEmptyFallback();
		}
	}

	toggleOption(option: any): void {
		const id = this.getId(option);
		this.selectedIds = this.isSelected(option)
			? this.selectedIds.filter(x => x !== id)
			: [...this.selectedIds, id];

		// Clearing the last tick leaves no filter at all, which for emptyMeansAll callers is the same
		// thing as everything. Jumping to everything on the spot undoes the click the user just made and
		// makes swapping one option for another awkward, so the empty state is kept while the panel is
		// open and close() turns it into everything. Callers where an empty list really does mean none
		// are left alone.
		if (this.emptyMeansAll && this.selectedIds.length === 0 && this.optionList.length > 0) {
			this.tickedToAll = false;
			this.holdingEmpty = true;
			return;
		}

		// Reaching "everything" by ticking options keeps the panel usable - with a short list that is one
		// click away, and locking the trigger there leaves no way back to a subset except finding the
		// "Select all" checkbox.
		this.tickedToAll = this.selectAllChecked;
		this.emitChange();
	}

	selectAll(): void {
		this.tickedToAll = false;
		this.selectedIds = this.options.map(option => this.getId(option));
		this.emitChange();
	}

	deselectAll(): void {
		this.tickedToAll = false;
		this.selectedIds = [];
		this.emitChange();
	}

	onSelectAllToggle(checked: boolean): void {
		if (checked) {
			this.setRememberedIds(this.selectedIds.length < this.optionList.length ? [...this.selectedIds] : []);
			this.selectAll();
			this.isOpen = false;
		} else {
			this.tickedToAll = false;
			this.restoreRememberedIds();
		}
	}

	get showEmptySelectionHint(): boolean {
		return this.emptyMeansAll && this.options.length > 0 && this.selectedIds.length === 0;
	}

	get selectAllChecked(): boolean {
		return this.options.length > 0 && this.selectedIds.length === this.options.length;
	}

	// "Select all" greys out the trigger and hides the panel. That only applies when everything was
	// handed over through the checkbox (or arrived that way from the form); an "all" the user ticked
	// option by option leaves the panel open so they can carry on adjusting it.
	get locked(): boolean {
		return this.selectAllChecked && !this.tickedToAll;
	}

	// The list the panel renders. Sortable dropdowns lift ticked options to the top in tick order.
	get displayOptions(): any[] {
		if (!this.sortable) {
			return this.options;
		}

		const ticked = this.selectedIds
			.map(id => this.options.find(option => this.getId(option) === id))
			.filter(option => !!option);
		return [...ticked, ...this.options.filter(option => !this.isSelected(option))];
	}

	// Drag and drop reordering (sortable only). Only ticked options can be moved, since the order is the
	// order of the ticks.
	dragIndex: number = null;
	dragOverIndex: number = null;

	onDragStart(event: DragEvent, index: number): void {
		this.dragIndex = index;
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = 'move';
			event.dataTransfer.setData('text/plain', String(index));
		}
	}

	onDragOver(event: DragEvent, index: number): void {
		if (this.dragIndex === null || index >= this.selectedIds.length) {
			return;
		}
		event.preventDefault();
		this.dragOverIndex = index;
	}

	onDrop(event: DragEvent, index: number): void {
		event.preventDefault();
		if (this.dragIndex !== null && index < this.selectedIds.length && index !== this.dragIndex) {
			const ids = [...this.selectedIds];
			const [moved] = ids.splice(this.dragIndex, 1);
			ids.splice(index, 0, moved);
			this.selectedIds = ids;
			this.emitChange();
		}
		this.onDragEnd();
	}

	onDragEnd(): void {
		this.dragIndex = null;
		this.dragOverIndex = null;
	}

	// Sorts the ticked options by label.
	sortSelected(direction: 'asc' | 'desc'): void {
		const labelOf = (id: string) => {
			const match = this.options.find(option => this.getId(option) === id);
			return match ? String(this.getLabel(match)) : '';
		};
		const sign = direction === 'asc' ? 1 : -1;
		this.selectedIds = [...this.selectedIds].sort((a, b) => sign * labelOf(a).localeCompare(labelOf(b), undefined, { numeric: true }));
		this.emitChange();
	}

	get selectedCount(): number {
		return this.selectedIds.length;
	}

	get totalCount(): number {
		return this.options.length;
	}

	get triggerLabel(): string {
		const total = this.options.length;
		const selected = this.selectedIds.length;

		if (selected === 0) {
			return this.placeholder;
		}

		if (this.allSelectedLabel && total > 0 && selected === total) {
			return this.allSelectedLabel;
		}

		if (selected === 1) {
			const match = this.options.find(option => this.getId(option) === this.selectedIds[0]);
			return match ? this.getLabel(match) : this.placeholder;
		}

		return `${selected} ${this.selectedCountLabel}`;
	}

	// Options can have been reloaded while "Select all" was on, so remembered ids are pruned against the
	// current list. Restoring a full list is skipped - that would just re-check "Select all" again.
	private restoreRememberedIds(): void {
		const availableIds = this.optionList.map(option => this.getId(option));
		const restored = this.rememberedIds.filter(id => availableIds.includes(id));

		this.selectedIds = restored.length < availableIds.length ? restored : [];
		this.emitChange();
	}

	private setRememberedIds(ids: string[]): void {
		this.rememberedIds = ids;
		if (this.persistKey) {
			this.userSessionService.setUserCarehomePreference(this.storageKey, ids);
		}
	}

	private readRememberedIds(): string[] {
		if (!this.persistKey) {
			return [];
		}

		const stored = this.userSessionService.getUserCarehomePreference<string[]>(this.storageKey);
		return Array.isArray(stored) ? stored : [];
	}

	private get storageKey(): string {
		return `multiSelect.${this.persistKey}`;
	}

	// Closing with nothing ticked: for these callers that means no filter, so say so by ticking everything.
	private applyEmptyFallback(): void {
		this.selectedIds = this.optionList.map(option => this.getId(option));
		this.tickedToAll = false;
		this.emitChange();
	}

	private sameIds(a: string[], b: string[]): boolean {
		return a.length === b.length && a.every(id => b.includes(id));
	}

	private applyRawValue(): void {
		if (this.holdingEmpty && this.optionList.length > 0) {
			this.selectedIds = [];
			return;
		}

		this.selectedIds = (this.emptyMeansAll && this.rawValue.length === 0 && this.options.length > 0)
			? this.options.map(option => this.getId(option))
			: this.rawValue;
	}

	private emitChange(): void {
		this.holdingEmpty = false;
		this.rawValue = (this.emptyMeansAll && this.options.length > 0 && this.selectedIds.length === this.options.length)
			? []
			: this.selectedIds;
		this.onChange(this.rawValue);
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		if (this.isOpen && !target.closest('.multi-select-dropdown')) {
			this.close();
		}
	}
}
